import { useEffect, useState, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageLoader, Spinner } from "@/components/Kit";
import { playSfx, playJingle } from "@/lib/sound";
import * as Icons from "lucide-react";
import { Gavel, Coins, Package, Clock, Tag, Zap } from "lucide-react";

function toPascal(s) { return (s || "").split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(""); }
const RARITY = { Junk: "#8892a6", Common: "#8892a6", Uncommon: "#2D9CFF", Rare: "#B14DFF", "Ultra-Rare": "#FF2D55", Legendary: "#FFC531" };
function fmt(s) { if (s <= 0) return "closing…"; const m = Math.floor(s / 60), r = s % 60; return `${m}:${String(r).padStart(2, "0")}`; }

export default function Auction() {
  const { user, patchUser } = useOutletContext();
  const [tab, setTab] = useState("browse");
  const [auctions, setAuctions] = useState(null);
  const [inv, setInv] = useState(null);
  const [bids, setBids] = useState({});
  const [busy, setBusy] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [form, setForm] = useState({ item_id: "", start_bid: "50", buyout: "", minutes: "10" });
  const baseTime = useRef(Date.now());

  const load = async () => {
    const [a, i] = await Promise.all([api.get("/auction"), api.get("/inventory")]);
    baseTime.current = Date.now();
    setAuctions(a.data.auctions); setInv(i.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  if (!auctions || !inv) return <PageLoader label="Opening the Auction House" />;

  const secLeft = (a) => Math.max(0, a.ends_in - Math.floor((now - baseTime.current) / 1000));

  const bid = async (a) => {
    const amount = parseInt(bids[a.auction_id], 10);
    if (!amount) { toast.error("Enter a bid amount."); return; }
    setBusy(a.auction_id);
    try {
      await api.post(`/auction/${a.auction_id}/bid`, { amount });
      patchUser({ credits: (user.credits || 0) - amount }); playSfx("coin");
      toast.success("Bid placed!"); setBids((b) => ({ ...b, [a.auction_id]: "" })); await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  const buyout = async (a) => {
    setBusy(a.auction_id);
    try {
      await api.post(`/auction/${a.auction_id}/buyout`, {});
      patchUser({ credits: (user.credits || 0) - a.buyout }); playJingle();
      toast.success(`Bought ${a.item.name}!`); await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  const create = async () => {
    if (!form.item_id) { toast.error("Pick an item to list."); return; }
    setBusy("create");
    try {
      await api.post("/auction/create", {
        item_id: form.item_id, start_bid: parseInt(form.start_bid, 10) || 1,
        buyout: parseInt(form.buyout, 10) || 0, minutes: parseInt(form.minutes, 10) || 10,
      });
      playSfx("pop"); toast.success("Listing created!");
      setForm({ item_id: "", start_bid: "50", buyout: "", minutes: "10" });
      setTab("browse"); await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };

  const listable = inv.items.filter((i) => i.category !== "scrap");

  return (
    <div data-testid="auction-page">
      <div className="flex items-end justify-between gap-4 mb-5 flex-wrap">
        <div>
          <span className="label-cap text-primary flex items-center gap-2"><Gavel className="w-4 h-4" /> Auction House // Auctioneer</span>
          <h1 className="text-4xl font-black tracking-tighter mt-2 font-head">Auction House</h1>
        </div>
        <div className="flex items-center gap-2 mono text-secondary font-bold"><Coins className="w-5 h-5" /> {user.credits}</div>
      </div>

      <div className="flex gap-1 mb-5 p-1 rounded-[var(--radius)] bg-muted w-fit">
        {[["browse", `Live (${auctions.length})`], ["list", "List an Item"]].map(([id, l]) => (
          <button key={id} data-testid={`auction-tab-${id}`} onClick={() => setTab(id)}
            className={`px-4 py-2 rounded-[var(--radius)] text-xs font-bold uppercase tracking-wider mono ${tab === id ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{l}</button>
        ))}
      </div>

      {tab === "browse" ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {auctions.length === 0 && <div className="col-span-full panel p-8 text-center text-muted-foreground">No live auctions. Be the first to list one!</div>}
          {auctions.map((a) => {
            const Icon = Icons[toPascal(a.item.icon)] || Package;
            const rc = RARITY[a.item.rarity] || "#8892a6";
            const left = secLeft(a);
            const minNext = a.current_bid + (a.has_bid ? a.min_increment : 0);
            return (
              <div key={a.auction_id} data-testid={`auction-${a.auction_id}`} className="panel p-4 flex flex-col">
                <div className="flex items-start justify-between mb-2">
                  <span className="w-10 h-10 rounded-[var(--radius)] grid place-items-center" style={{ background: `${rc}22`, color: rc }}><Icon className="w-5 h-5" /></span>
                  <span className={`label-cap text-[9px] flex items-center gap-1 ${left < 15 ? "text-accent" : ""}`}><Clock className="w-3 h-3" />{fmt(left)}</span>
                </div>
                <h3 className="font-head font-bold text-sm">{a.item.name}</h3>
                <p className="text-[11px] text-muted-foreground">by {a.seller_name}</p>
                <div className="text-sm mono mt-2">
                  <div className="flex items-center gap-1"><Tag className="w-3.5 h-3.5 text-secondary" /> {a.current_bid}c {a.has_bid ? `(${a.top_bidder_name})` : "(start)"}</div>
                  {a.buyout > 0 && <div className="flex items-center gap-1 text-accent"><Zap className="w-3.5 h-3.5" /> Buyout {a.buyout}c</div>}
                </div>
                <div className="mt-3 flex gap-2">
                  <input data-testid={`bid-input-${a.auction_id}`} type="number" placeholder={`≥ ${minNext}`} value={bids[a.auction_id] || ""}
                    onChange={(e) => setBids((b) => ({ ...b, [a.auction_id]: e.target.value }))}
                    className="w-full px-2 py-2 rounded-[var(--radius)] bg-muted border border-border mono text-sm min-w-0 focus:outline-none focus:border-primary" />
                  <button onClick={() => bid(a)} data-testid={`bid-btn-${a.auction_id}`} disabled={busy === a.auction_id} className="btn-primary text-xs px-3 shrink-0">Bid</button>
                </div>
                {a.buyout > 0 && <button onClick={() => buyout(a)} data-testid={`buyout-btn-${a.auction_id}`} disabled={busy === a.auction_id} className="btn-secondary w-full mt-2 text-xs">Buy Now · {a.buyout}c</button>}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="max-w-md">
          <div className="panel-glow p-5 hud-corner space-y-4">
            <div>
              <label className="label-cap block mb-2">Item to List</label>
              <select data-testid="list-item-select" value={form.item_id} onChange={(e) => setForm((f) => ({ ...f, item_id: e.target.value }))}
                className="w-full px-3 py-3 rounded-[var(--radius)] bg-muted border border-border mono focus:outline-none focus:border-primary">
                <option value="">— pick from inventory —</option>
                {listable.map((i) => <option key={i.id} value={i.id}>{`${i.name} (x${i.qty})`}</option>)}
              </select>
              {listable.length === 0 && <p className="text-xs text-muted-foreground mt-1">No listable items. Buy gear at the Bazaar first.</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-cap block mb-2">Start Bid</label>
                <input data-testid="list-startbid-input" type="number" min="1" value={form.start_bid} onChange={(e) => setForm((f) => ({ ...f, start_bid: e.target.value }))}
                  className="w-full px-3 py-3 rounded-[var(--radius)] bg-muted border border-border mono focus:outline-none focus:border-primary" />
              </div>
              <div>
                <label className="label-cap block mb-2">Buyout (0=none)</label>
                <input data-testid="list-buyout-input" type="number" min="0" value={form.buyout} onChange={(e) => setForm((f) => ({ ...f, buyout: e.target.value }))}
                  className="w-full px-3 py-3 rounded-[var(--radius)] bg-muted border border-border mono focus:outline-none focus:border-primary" />
              </div>
            </div>
            <div>
              <label className="label-cap block mb-2">Duration (minutes)</label>
              <input data-testid="list-minutes-input" type="number" min="1" value={form.minutes} onChange={(e) => setForm((f) => ({ ...f, minutes: e.target.value }))}
                className="w-full px-3 py-3 rounded-[var(--radius)] bg-muted border border-border mono focus:outline-none focus:border-primary" />
            </div>
            <button onClick={create} data-testid="list-create-btn" disabled={busy === "create"} className="btn-primary w-full py-3">
              {busy === "create" ? <Spinner className="w-4 h-4" /> : <><Gavel className="w-4 h-4" /> Create Listing</>}
            </button>
            <p className="text-xs text-muted-foreground">Anti-snipe: any bid in the final seconds extends the clock. Buyout ends the auction instantly.</p>
          </div>
        </div>
      )}
    </div>
  );
}
